import { h, Fragment } from 'preact'
import { useSignal } from '@preact/signals'
import { MemberPicker } from './MemberPicker.jsx'
import { ModalShell } from './ModalShell.jsx'
import { chatApi } from '../chat-api.js'
import { t } from '../chat-i18n.js'

/**
 * NewDirectModal — pick one person → create/open DM via chatApi.rooms.direct.
 *
 * Props: { open, onClose, onCreated(room) }
 */
export function NewDirectModal({ open, onClose, onCreated }) {
  const selected = useSignal([])
  const saving   = useSignal(false)
  const error    = useSignal(null)

  function reset() {
    selected.value = []
    saving.value = false
    error.value = null
  }

  function handleClose() {
    reset()
    onClose?.()
  }

  async function handleSubmit(e) {
    e?.preventDefault()
    const person = selected.value[0]
    if (!person || saving.value) return

    saving.value = true
    error.value = null
    try {
      const res = await chatApi.rooms.direct(person.id)
      if (!res.ok) {
        const body = await res.json.catch(() => ({}))
        error.value = body.error || t('chat.rooms.not_authorized')
        return
      }
      const room = await res.json
      reset()
      onCreated?.(room)
      onClose?.()
    } catch {
      error.value = t('chat.rooms.not_authorized')
    } finally {
      saving.value = false
    }
  }

  const canSubmit = selected.value.length === 1 && !saving.value

  return (
    <ModalShell
      open={open}
      onClose={handleClose}
      title={t('chat.rooms.new_direct_title')}
      iconClass="bi-person-plus"
      footer={
        <>
          <button type="button" class="btn btn-ghost-secondary" onClick={handleClose} disabled={saving.value}>
            {t('chat.rooms.cancel')}
          </button>
          <button type="button" class="btn btn-primary" onClick={handleSubmit} disabled={!canSubmit}>
            {saving.value
              ? <span class="spinner-border spinner-border-sm" role="status" />
              : t('chat.rooms.send')}
          </button>
        </>
      }
    >
      <p class="text-muted small mb-3">{t('chat.rooms.recipient_label')}</p>
      {error.value && <div class="alert alert-danger py-2 small">{error.value}</div>}
      <MemberPicker
        selected={selected.value}
        onChange={persons => { selected.value = persons }}
        multiple={false}
        disabled={saving.value}
      />
    </ModalShell>
  )
}
